import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http'
import { map, take } from 'rxjs/operators'
import { ReplaySubject } from 'rxjs';
import { FeatureFlagService } from '../feature-flag.service';
import { Firestore, collection, doc, where, query, documentId, getDocs, QuerySnapshot, updateDoc, getDoc, setDoc, deleteDoc } from '@angular/fire/firestore';
import { snapshot$ } from 'src/firebase-utils';
import { HandleErrorService } from './log.service';
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);


  errorHandler = inject(HandleErrorService)
  firestore = inject(Firestore)
  featureFlagService = inject(FeatureFlagService)

  userRef = collection(this.firestore, 'users')

  server: string = 'https://us-central1-angular-pwa-cf61b.cloudfunctions.net/api'
  key: string = '47e1f7989d2f7499997452c7e11e90c6'


  userSource = new ReplaySubject<any>()
  user$ = this.userSource.asObservable()


  userState: WritableSignal<any> = signal(null)

  // Local _user for assigning local observable
  _userSource = new ReplaySubject<any>()
  _user$ = this._userSource.asObservable()

  private _uid: any

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);
  constructor() {

    this._user$.subscribe((response: any) => {

      this._uid = response

      if(!!this._uid) {

        const docRef = doc(this.firestore, 'users', response)

        snapshot$(docRef).pipe(take(1)).subscribe((user: {[key: string]: any}) => {

          const featureFlagQuery = query(this.featureFlagService.featureFlagRef, where(documentId(), '==', user.accessLevel));

          getDocs(featureFlagQuery).then((querySnapshot: QuerySnapshot) => {
            const featureFlags = querySnapshot.docs.map(doc => doc.data());
            
            this.featureFlagService.data.set(featureFlags[0])
            this.userState.set(user)
            this.userSource.next(user)

          }).catch((err) => {
            this.errorHandler.log(err)
          });

        })

      } else {
        this.userState.set(null)
        this.userSource.next(undefined)
      }
    })
  }

  async getUserAccessLevel() {

    try {
      const docRef = doc(this.firestore, 'users', this._uid)
      const docSnap = await getDoc(docRef)
      
      if(docSnap.exists()) {
        return docSnap.data().accessLevel;
      }

      throw new Error('Document does not exist');
      
    } catch(err) {
      this.errorHandler.log(err)
    }
  }

  // Handlers

  /**
   * Returns the user id to check if a user id session exists, for actual user data, subscribe to user$
   */
  get user() {
    return this._uid
  }

  /**
   * Sets the user id, which also triggers the user$ subscription
   */
  set user(id) {
    this._userSource.next(id)
  }

  /**
   * Gets all users, this function is not currently used, but should be used for pruning in the future
   */
  getUsers() {

    let params = {
      key: this.key
    }

    return this.http.post(this.server + '/read/users', {params: params})
  }


  /**
   * Creates an auth user with a randomly generated password
   * 
   * @param value 
   */
  createUser(value) {

    let params = {
      key: this.key,
      ...value
    }

    return this.http.post(this.server + '/create/user', {params: params})
      .pipe(map((response: any) => {

        let avatarParams = {
          key: this.key,
          id: response.uid
        }

        this.http.post(this.server + '/create/avatar', {params: avatarParams}).subscribe((resp) => {
          console.log(resp)
        })


        return response
      }))
  }


  /**
   * Deletes the auth user
   * 
   * @param id 
   */
  deleteUser(id) {

    let params = {
      key: this.key,
      id: id
    }

    return this.http.post(this.server + '/delete/user', {params: params})
  }


  /**
   * Toggles enable/disable state on user
   * @param id 
   */
  disableUser(id) {
    let params = {
      key: this.key,
      id: id
    }

    return this.http.post(this.server + '/update/user/disable', {params: params})
  }


  /**
   * Toggles enable/disable state on user
   * @param id 
   */
  enableUser(id) {
    let params = {
      key: this.key,
      id: id
    }

    return this.http.post(this.server + '/update/user/enable', {params: params})
  }


  /**
   * Generates a new very long password on the user. User will not be able to log in until they obtain their new password
   * 
   * @param id 
   */
  generatePassword(id) {

    let params = {
      key: this.key,
      id: id
    }

    return this.http.post(this.server + '/update/user/password', {params: params})
  }


  async updateFirebaseUser(data: any, uid?) {
    
    try {
      const docRef = doc(this.firestore, 'users', uid || this._uid)
      await updateDoc(docRef, data)
    } catch(err) {
      this.errorHandler.log(err)
    }
  }

  async setFirebaseUser(uid: string, data: any) {
    try {
      const docRef = doc(this.firestore, 'users', uid)
      await setDoc(docRef, data)
    } catch(err) {
      this.errorHandler.log(err)
    }
  }

  async deleteFirebaseUser(uid: string) {
    try {
      const decRef = doc(this.firestore, 'users', uid)
      await deleteDoc(decRef)
    } catch(err) {
      this.errorHandler.log(err)
    }
  }
}
